import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Loader2, AlertCircle, XCircle } from 'lucide-react';
import { v1Api } from '../../../services/api';

/**
 * PaymentVerificationModal Component
 * 
 * Supports both Hospital and Fleet dashboards with separate endpoint integration.
 * Both endpoints are retained and used based on the dashboardType prop.
 * 
 * Endpoints:
 *   - Hospital: POST /v1/hospital/payments/verify
 *     Used when dashboardType='hospital' (default)
 *     Request: { orderId, paymentId, signature }
 *     Response: { success, message, data: { orderId, paymentId, amount, status, bookingId, paymentOrder } }
 * 
 *   - Fleet: POST /v1/fleet/payments/verify
 *     Used when dashboardType='fleet'
 *     Request: { orderId, paymentId, signature }
 *     Response: { success, message, data: { orderId, paymentId, amount, status, bookingId, paymentOrder } }
 * 
 * Purpose: Verifies payment after Razorpay completion
 * 
 * @param {string} dashboardType - 'hospital' or 'fleet' (default: 'hospital')
 *                                 Determines which endpoint to use for verification
 */
const PaymentVerificationModal = ({
  isOpen,
  onClose,
  orderId,
  paymentId,
  signature,
  bookingId,
  onVerificationSuccess,
  onVerificationFailure,
  autoVerify = true,
  dashboardType = 'hospital' // 'hospital' or 'fleet'
}) => {
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null); // 'success', 'failed', null
  const [error, setError] = useState(null);
  const [verificationData, setVerificationData] = useState(null);

  // Auto-verify on mount if all data is available
  useEffect(() => {
    if (isOpen && autoVerify && orderId && paymentId && signature) {
      handleVerifyPayment();
    }
  }, [isOpen, autoVerify, orderId, paymentId, signature]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      requestAnimationFrame(() => {
        document.body.style.overflow = 'hidden';
      });
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      requestAnimationFrame(() => {
        document.body.style.overflow = 'unset';
      });
    };
  }, [isOpen, loading, onClose]);

  const handleVerifyPayment = async () => {
    if (!orderId || !paymentId || !signature) {
      const errMsg = 'Missing payment details. Please try again.';
      setError(errMsg);
      if (onVerificationFailure) onVerificationFailure(errMsg);
      return;
    }

    setLoading(true);
    setError(null);
    setVerificationStatus(null);

    try {
      // Prepare request body
      const requestBody = {
        orderId: orderId,
        paymentId: paymentId,
        signature: signature
      };

      console.log('💳 [PaymentVerificationModal] Verifying payment:', {
        orderId,
        paymentId,
        signature: signature.substring(0, 20) + '...',
        dashboardType
      });

      // Call the appropriate payment verification endpoint based on dashboard type
      // Both endpoints are supported and retained:
      // - Hospital: POST /v1/hospital/payments/verify (for hospital dashboard)
      // - Fleet: POST /v1/fleet/payments/verify (for fleet dashboard)
      // The endpoint selection is based on the dashboardType prop
      let endpoint;
      if (dashboardType === 'fleet') {
        // Fleet-specific payment verification endpoint
        // Endpoint: POST /v1/fleet/payments/verify
        // Request body: { orderId, paymentId, signature }
        // Response: { success, message, data: { orderId, paymentId, amount, status, bookingId, paymentOrder } }
        endpoint = '/v1/fleet/payments/verify';
        console.log('💳 [PaymentVerificationModal] Using Fleet endpoint: POST', endpoint);
        console.log('💳 [PaymentVerificationModal] Fleet request body:', {
          orderId: requestBody.orderId,
          paymentId: requestBody.paymentId,
          signature: requestBody.signature.substring(0, 20) + '...'
        });
      } else {
        // Hospital-specific payment verification endpoint (default)
        // Endpoint: POST /v1/hospital/payments/verify
        endpoint = '/v1/hospital/payments/verify';
        console.log('💳 [PaymentVerificationModal] Using Hospital endpoint: POST', endpoint);
      }

      // Both endpoints expect the same request body structure:
      // { orderId, paymentId, signature }
      // Both endpoints return the same response structure
      const response = await v1Api.post(endpoint, requestBody);

      // Handle response
      // Expected response structure (same for both Hospital and Fleet endpoints):
      // 
      // Fleet endpoint response example:
      // {
      //   "success": true,
      //   "message": "Payment verified successfully",
      //   "data": {
      //     "orderId": "order_RtQJdbqA05smDE",
      //     "paymentId": "pay_RtQKIA1t9JaMUt",
      //     "amount": 1799,
      //     "status": "PAID",
      //     "bookingId": "806849",
      //     "paymentOrder": {
      //       "orderId": "order_RtQJdbqA05smDE",
      //       "status": "PAID"
      //     }
      //   }
      // }
      //
      // Hospital endpoint response example:
      // {
      //   "success": true,
      //   "message": "Payment verified successfully",
      //   "data": {
      //     "orderId": "order_RsZIoHlE0OKxDQ",
      //     "paymentId": "pay_RsZJau8cv1p5eu",
      //     "amount": 1271,
      //     "status": "PAID",
      //     "bookingId": "802400",
      //     "paymentOrder": {
      //       "orderId": "order_RsZIoHlE0OKxDQ",
      //       "status": "PAID"
      //     }
      //   }
      // }
      // Note: Both Hospital and Fleet endpoints return the same response structure
      const result = response.data || response;

      console.log('💳 [PaymentVerificationModal] Full response:', result);

      // Validate response structure
      if (!result?.success) {
        const errorMsg = result?.message || result?.error || 'Payment verification failed';
        setVerificationStatus('failed');
        setError(errorMsg);
        if (onVerificationFailure) onVerificationFailure(errorMsg, result);
        setLoading(false);
        return;
      }

      if (!result?.data) {
        const errorMsg = 'Invalid response: missing data field';
        setVerificationStatus('failed');
        setError(errorMsg);
        if (onVerificationFailure) onVerificationFailure(errorMsg, result);
        setLoading(false);
        return;
      }

      // Extract response data
      const { orderId: responseOrderId, paymentId: responsePaymentId, amount, status, bookingId: responseBookingId, paymentOrder } = result.data;

      // Validate payment status - check both paymentOrder.status and data.status
      const paymentStatus = paymentOrder?.status || status;

      if (!paymentStatus) {
        const errorMsg = 'Invalid response: missing payment status';
        setVerificationStatus('failed');
        setError(errorMsg);
        if (onVerificationFailure) onVerificationFailure(errorMsg, result);
        setLoading(false);
        return;
      }

      if (paymentStatus !== 'PAID') {
        const errorMsg = `Payment verification failed: Payment status is ${paymentStatus}, not PAID.`;
        setVerificationStatus('failed');
        setError(errorMsg);
        if (onVerificationFailure) onVerificationFailure(errorMsg, result);
        setLoading(false);
        return;
      }

      console.log('✅ [PaymentVerificationModal] Payment verified successfully');
      console.log('💳 [PaymentVerificationModal] Dashboard Type:', dashboardType);
      console.log('💳 [PaymentVerificationModal] Order ID:', responseOrderId);
      console.log('💳 [PaymentVerificationModal] Payment ID:', responsePaymentId);
      console.log('💳 [PaymentVerificationModal] Amount:', amount);
      console.log('💳 [PaymentVerificationModal] Status:', paymentStatus);
      console.log('💳 [PaymentVerificationModal] Booking ID:', responseBookingId);

      // Success - store full verification data
      setVerificationStatus('success');
      setVerificationData({
        ...result.data,
        bookingId: responseBookingId || bookingId // Use response bookingId if available, fallback to prop
      });
      
      if (onVerificationSuccess) {
        onVerificationSuccess(result.data);
      }

      // Auto-close after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('❌ [PaymentVerificationModal] Payment verification error:', err);
      console.error('❌ [PaymentVerificationModal] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      const errMsg = err.response?.data?.message || 
                    err.response?.data?.error || 
                    err.message || 
                    'Payment verification failed. Please contact support.';
      setVerificationStatus('failed');
      setError(errMsg);
      setLoading(false);
      if (onVerificationFailure) onVerificationFailure(errMsg, err);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading && verificationStatus) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg max-w-md w-full mx-auto shadow-xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className={`p-4 rounded-t-lg border-b ${
          verificationStatus === 'success' 
            ? 'bg-green-50 border-green-200' 
            : verificationStatus === 'failed'
            ? 'bg-red-50 border-red-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`p-1.5 rounded-full ${
                verificationStatus === 'success' 
                  ? 'bg-green-100' 
                  : verificationStatus === 'failed'
                  ? 'bg-red-100'
                  : 'bg-blue-100'
              }`}>
                {verificationStatus === 'success' ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : verificationStatus === 'failed' ? (
                  <XCircle className="w-4 h-4 text-red-600" />
                ) : (
                  <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                )}
              </div>
              <div className="ml-2">
                <h3 className={`text-base font-semibold ${
                  verificationStatus === 'success' 
                    ? 'text-green-900' 
                    : verificationStatus === 'failed'
                    ? 'text-red-900'
                    : 'text-blue-900'
                }`}>
                  {verificationStatus === 'success' 
                    ? 'Payment Verified' 
                    : verificationStatus === 'failed'
                    ? 'Verification Failed'
                    : 'Verifying Payment'}
                </h3>
                <p className={`text-xs ${
                  verificationStatus === 'success' 
                    ? 'text-green-700' 
                    : verificationStatus === 'failed'
                    ? 'text-red-700'
                    : 'text-blue-700'
                }`}>
                  {verificationStatus === 'success' 
                    ? 'Your payment has been confirmed' 
                    : verificationStatus === 'failed'
                    ? 'Payment verification unsuccessful'
                    : 'Please wait while we verify your payment'}
                </p>
              </div>
            </div>
            {(verificationStatus || !loading) && (
              <button
                onClick={onClose}
                className={`transition-colors ${
                  verificationStatus === 'success' 
                    ? 'text-green-400 hover:text-green-600' 
                    : verificationStatus === 'failed'
                    ? 'text-red-400 hover:text-red-600'
                    : 'text-blue-400 hover:text-blue-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Loading State */}
          {loading && !verificationStatus && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-sm text-gray-600">Verifying payment with server...</p>
            </div>
          )}

          {/* Success State */}
          {verificationStatus === 'success' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-green-900 mb-2">
                  Payment Verified Successfully!
                </h4>
                <p className="text-sm text-green-700">
                  Your payment has been confirmed and your booking is now active.
                </p>
              </div>

              {verificationData && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment ID:</span>
                    <span className="font-mono font-medium text-gray-900">
                      {verificationData.paymentId || paymentId}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order ID:</span>
                    <span className="font-mono font-medium text-gray-900">
                      {verificationData.orderId || orderId}
                    </span>
                  </div>
                  {verificationData.bookingId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Booking ID:</span>
                      <span className="font-mono font-medium text-gray-900">
                        {verificationData.bookingId}
                      </span>
                    </div>
                  )}
                  {verificationData.amount && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-medium text-gray-900">
                        ₹{(verificationData.amount / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {verificationData.status && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium text-green-600">
                        {verificationData.status}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Failure State */}
          {verificationStatus === 'failed' && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-red-900 mb-1">
                      Verification Failed
                    </h4>
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment ID:</span>
                  <span className="font-mono font-medium text-gray-900">{paymentId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Order ID:</span>
                  <span className="font-mono font-medium text-gray-900">{orderId}</span>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  If you believe this is an error, please contact support with your payment ID.
                </p>
              </div>
            </div>
          )}

          {/* Manual Verification (if auto-verify is disabled) */}
          {!autoVerify && !verificationStatus && !loading && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Order ID:</span>
                  <span className="font-mono font-medium text-gray-900">{orderId || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment ID:</span>
                  <span className="font-mono font-medium text-gray-900">{paymentId || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Signature:</span>
                  <span className="font-mono font-medium text-gray-900 text-xs">
                    {signature ? signature.substring(0, 20) + '...' : 'N/A'}
                  </span>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {(!loading || verificationStatus) && (
          <div className="bg-gray-50 px-4 py-3 rounded-b-lg">
            <div className="flex justify-end">
              {verificationStatus === 'failed' && !autoVerify && (
                <button
                  onClick={handleVerifyPayment}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Retry Verification
                </button>
              )}
              {verificationStatus && (
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {verificationStatus === 'success' ? 'Continue' : 'Close'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentVerificationModal;

